import { supabase } from '../lib/supabase';
import { overtimeService } from './overtimeService';
import { getThumbnailPath } from '../utils/imageUtils';

export const contractorJobService = {
  // Get contractor's assigned job instances
  async getContractorJobs(contractorId, date = null) {
    let query = supabase?.from('job_instances')?.select(`
      *,
      job:jobs(
        *,
        client:clients(id, client_name),
        job_site:job_sites(id, site_name, address, address_lat, address_lng)
      )
    `)?.eq('assigned_contractor', contractorId)?.in('status', ['assigned', 'in_progress', 'submitted']);

    if (date) {
      query = query?.eq('scheduled_date', date);
    }

    query = query?.order('scheduled_date', { ascending: true });

    const { data, error } = await query;
    if (error) throw error;
    
    return data?.map(instance => ({
      id: instance?.id,
      jobId: instance?.job_id,
      job: instance?.job,
      scheduledDate: instance?.scheduled_date,
      scheduledTime: instance?.scheduled_time,
      status: instance?.status,
      clockInTime: instance?.clock_in_time,
      clockOutTime: instance?.clock_out_time,
      workNotes: instance?.work_notes,
      checklistData: instance?.checklist_data,
      questionnaireResponses: instance?.questionnaire_responses,
      is_pending: instance?.is_pending,
      is_pending_report: instance?.is_pending_report
    }));
  },

  // Get specific job instance details
  async getJobInstanceDetails(instanceId) {
    const { data, error } = await supabase?.from('job_instances')?.select(`
      *,
      job:jobs(
        *,
        client:clients(id, client_name, contact_person, contact_email, contact_phone),
        job_site:job_sites(id, site_name, address, address_lat, address_lng, contact_person, contact_email, contact_phone)
      ),
      photos:job_photos(*)
    `)?.eq('id', instanceId)?.single();

    if (error) throw error;
    
    return {
      id: data?.id,
      jobId: data?.job_id,
      job: data?.job,
      scheduledDate: data?.scheduled_date,
      scheduledTime: data?.scheduled_time,
      status: data?.status,
      clockInTime: data?.clock_in_time,
      clockOutTime: data?.clock_out_time,
      workNotes: data?.work_notes,
      checklistData: data?.checklist_data,
      questionnaireResponses: data?.questionnaire_responses,
      photos: data?.photos,
      notes: data?.notes,
      assignedContractor: data?.assigned_contractor,
      is_pending: data?.is_pending,
      is_pending_report: data?.is_pending_report
    };
  },

  // Clock in
  async clockIn(instanceId, latitude, longitude) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('job_instances')?.update({
        clock_in_time: new Date()?.toISOString(),
        clock_in_location: latitude && longitude ? `(${latitude},${longitude})` : null,
        status: 'in_progress'
      })?.eq('id', instanceId)?.eq('assigned_contractor', user?.id)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Manual clock in (for late submissions with poor connectivity)
  async clockInManual(instanceId, latitude, longitude, manualClockInTime) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Build update object - is_late_submission column may not exist if migration hasn't run
    // IMPORTANT: Clear clock_out_time to avoid constraint violation
    // (job_instances_valid_clock_times requires clock_out >= clock_in)
    const updateData = {
      clock_in_time: manualClockInTime,
      clock_in_location: latitude && longitude ? `(${latitude},${longitude})` : null,
      clock_out_time: null,  // Clear any existing clock_out to avoid constraint violation
      clock_out_location: null,
      status: 'in_progress'
    };

    // Try with is_late_submission first
    let { data, error } = await supabase?.from('job_instances')?.update({
        ...updateData,
        is_late_submission: true
      })?.eq('id', instanceId)?.eq('assigned_contractor', user?.id)?.select()?.single();

    // If column doesn't exist, retry without it
    if (error && error.message?.includes('is_late_submission')) {
      console.warn('is_late_submission column not found, proceeding without it');
      const result = await supabase?.from('job_instances')?.update(updateData)
        ?.eq('id', instanceId)?.eq('assigned_contractor', user?.id)?.select()?.single();
      data = result.data;
      error = result.error;
    }

    if (error) throw error;
    return data;
  },

  // Clock out and submit report with payment cap
  // Returns { instance, timesheet, hasOvertime, overtimeInfo } if overtime detected
  async clockOut(instanceId, latitude, longitude, workNotes, checklistData, questionnaireResponses, overtimeData = null, manualClockOutTime = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Use manual clock-out time if provided, otherwise use current time
    const clockOutTime = manualClockOutTime || new Date()?.toISOString();

    // Build update object - is_late_submission column may not exist if migration hasn't run
    const updateData = {
      clock_out_time: clockOutTime,
      clock_out_location: latitude && longitude ? `(${latitude},${longitude})` : null,
      work_notes: workNotes,
      checklist_data: checklistData,
      questionnaire_responses: questionnaireResponses,
      status: 'submitted'
    };

    // Try with is_late_submission first if this is a manual submission
    let instance, instanceError;
    if (manualClockOutTime) {
      const result = await supabase?.from('job_instances')?.update({
          ...updateData,
          is_late_submission: true
        })?.eq('id', instanceId)?.eq('assigned_contractor', user?.id)?.select(`
          *,
          job:jobs!inner(payment_type, payment_amount, estimated_hours)
        `)?.single();
      instance = result.data;
      instanceError = result.error;

      // If column doesn't exist, retry without it
      if (instanceError && instanceError.message?.includes('is_late_submission')) {
        console.warn('is_late_submission column not found, proceeding without it');
        const retryResult = await supabase?.from('job_instances')?.update(updateData)
          ?.eq('id', instanceId)?.eq('assigned_contractor', user?.id)?.select(`
            *,
            job:jobs!inner(payment_type, payment_amount, estimated_hours)
          `)?.single();
        instance = retryResult.data;
        instanceError = retryResult.error;
      }
    } else {
      // Regular clock out without is_late_submission flag
      const result = await supabase?.from('job_instances')?.update(updateData)
        ?.eq('id', instanceId)?.eq('assigned_contractor', user?.id)?.select(`
          *,
          job:jobs!inner(payment_type, payment_amount, estimated_hours)
        `)?.single();
      instance = result.data;
      instanceError = result.error;
    }

    if (instanceError) throw instanceError;

    // Calculate hours worked
    const clockInTime = new Date(instance.clock_in_time);
    const clockOutTimeDate = new Date(clockOutTime);
    const actualHours = (clockOutTimeDate - clockInTime) / (1000 * 60 * 60);

    // Get payment configuration from job
    const paymentType = instance.job?.payment_type || 'hourly';
    const paymentAmount = parseFloat(instance.job?.payment_amount) || 0;
    const estimatedHours = instance.job?.estimated_hours ? parseFloat(instance.job.estimated_hours) : null;

    // Calculate amount earned with payment cap for hourly jobs
    let amountEarned = 0;
    let cappedHours = null;
    let hasOvertime = false;
    let overtimeInfo = null;

    if (paymentType === 'hourly') {
      // Use overtimeService to calculate with rounding rules and cap
      const calculation = overtimeService.calculateCappedPayment(actualHours, estimatedHours, paymentAmount);

      cappedHours = calculation.cappedHours;
      amountEarned = calculation.basePay;
      hasOvertime = calculation.hasOvertime;

      if (hasOvertime) {
        overtimeInfo = {
          estimatedHours: calculation.estimatedHours,
          actualHours: calculation.billableHours,
          overtimeHours: calculation.overtimeHours,
          hourlyRate: calculation.hourlyRate,
          basePay: calculation.basePay,
          potentialOvertimePay: calculation.overtimeAmount,
          totalPotential: calculation.totalPotential
        };
      }
    } else {
      // Fixed rate - full amount regardless of hours
      amountEarned = paymentAmount;
      cappedHours = actualHours;
    }

    // Check if a timesheet already exists for this job instance
    const { data: existingTimesheets } = await supabase
      ?.from('timesheets')
      ?.select('id')
      ?.eq('job_instance_id', instanceId)
      ?.limit(1);

    const existingTimesheetId = existingTimesheets?.[0]?.id;

    let timesheet;
    let timesheetError;

    if (existingTimesheetId) {
      // Update existing timesheet instead of creating a new one
      const result = await supabase?.from('timesheets')?.update({
          clock_in_time: instance?.clock_in_time,
          clock_out_time: clockOutTime,
          total_hours: parseFloat(actualHours.toFixed(2)),
          estimated_hours: estimatedHours,
          capped_hours: parseFloat(cappedHours.toFixed(2)),
          base_amount: parseFloat(amountEarned.toFixed(2)),
          amount_earned: parseFloat(amountEarned.toFixed(2)),
          overtime_amount: 0,
          has_overtime_request: false,
          status: 'pending',
          updated_at: new Date().toISOString()
        })?.eq('id', existingTimesheetId)?.select()?.single();
      timesheet = result.data;
      timesheetError = result.error;
    } else {
      // Create new timesheet if none exists
      const result = await supabase?.from('timesheets')?.insert({
          job_instance_id: instanceId,
          contractor_id: user?.id,
          company_id: instance?.company_id,
          clock_in_time: instance?.clock_in_time,
          clock_out_time: clockOutTime,
          total_hours: parseFloat(actualHours.toFixed(2)),
          estimated_hours: estimatedHours,
          capped_hours: parseFloat(cappedHours.toFixed(2)),
          base_amount: parseFloat(amountEarned.toFixed(2)),
          amount_earned: parseFloat(amountEarned.toFixed(2)),
          overtime_amount: 0,
          has_overtime_request: false,
          status: 'pending'
        })?.select()?.single();
      timesheet = result.data;
      timesheetError = result.error;
    }

    if (timesheetError) throw timesheetError;

    // If overtime data was provided and there's overtime, create the request
    if (hasOvertime && overtimeData?.requestOvertime && overtimeData?.justification) {
      const overtimeRequest = await overtimeService.createOvertimeRequest({
        jobInstanceId: instanceId,
        timesheetId: timesheet.id,
        contractorId: user?.id,
        companyId: instance?.company_id,
        estimatedHours: overtimeInfo.estimatedHours,
        actualHours: overtimeInfo.actualHours,
        overtimeHours: overtimeInfo.overtimeHours,
        hourlyRate: overtimeInfo.hourlyRate,
        basePay: overtimeInfo.basePay,
        requestedOvertimeAmount: overtimeInfo.potentialOvertimePay,
        justification: overtimeData.justification
      });

      return { instance, timesheet, hasOvertime, overtimeInfo, overtimeRequest };
    }

    return { instance, timesheet, hasOvertime, overtimeInfo };
  },

  // Resubmit a report that was rejected or disputed
  // Updates the report data and changes status back to 'submitted'
  // Optionally accepts edited clock in/out times and updates the timesheet accordingly
  async resubmitReport(instanceId, workNotes, checklistData, questionnaireResponses, editedClockIn = null, editedClockOut = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Build update object
    const updateData = {
      work_notes: workNotes,
      checklist_data: checklistData,
      questionnaire_responses: questionnaireResponses,
      status: 'submitted',
      updated_at: new Date().toISOString()
    };

    // Add edited times if provided
    if (editedClockIn) {
      updateData.clock_in_time = new Date(editedClockIn).toISOString();
    }
    if (editedClockOut) {
      updateData.clock_out_time = new Date(editedClockOut).toISOString();
    }

    // Update the report data and change status to submitted
    // Try with notes field first (to clear rejection/dispute feedback)
    // Also fetch job payment info for timesheet recalculation
    let { data, error } = await supabase
      ?.from('job_instances')
      ?.update({
        ...updateData,
        notes: null // Clear the rejection/dispute feedback
      })
      ?.eq('id', instanceId)
      ?.eq('assigned_contractor', user?.id)
      ?.in('status', ['needs_revision', 'disputed']) // Only allow for these statuses
      ?.select(`
        *,
        job:jobs(payment_type, payment_amount, estimated_hours)
      `)
      ?.single();

    // If notes column doesn't exist, retry without it
    if (error && (error.message?.includes('notes') || error.message?.includes('schema cache') || error.code === '42703')) {
      console.warn('notes column not found, proceeding without clearing it. Error:', error.message);
      const retryResult = await supabase
        ?.from('job_instances')
        ?.update(updateData)
        ?.eq('id', instanceId)
        ?.eq('assigned_contractor', user?.id)
        ?.in('status', ['needs_revision', 'disputed'])
        ?.select(`
          *,
          job:jobs(payment_type, payment_amount, estimated_hours)
        `)
        ?.single();

      data = retryResult.data;
      error = retryResult.error;
    }

    if (error) throw error;
    if (!data) throw new Error('Job not found or not in a resubmittable state');

    // Always update the timesheet on resubmission - recalculate based on current times
    // Get the most recent timesheet for this job instance (there may be multiple from previous submissions)
    const { data: timesheets } = await supabase
      ?.from('timesheets')
      ?.select('*')
      ?.eq('job_instance_id', instanceId)
      ?.order('created_at', { ascending: false })
      ?.limit(1);

    const existingTimesheet = timesheets?.[0];

    // Track overtime info for return
    let hasOvertime = false;
    let overtimeInfo = null;

    if (existingTimesheet) {
      // Calculate hours worked based on current clock times
      const clockInTime = new Date(data.clock_in_time);
      const clockOutTime = new Date(data.clock_out_time);
      const actualHours = (clockOutTime - clockInTime) / (1000 * 60 * 60);

      // Get payment configuration from job
      const paymentType = data.job?.payment_type || 'hourly';
      const paymentAmount = parseFloat(data.job?.payment_amount) || 0;
      const estimatedHours = data.job?.estimated_hours ? parseFloat(data.job.estimated_hours) : null;

      // Calculate amount earned with payment cap for hourly jobs
      let amountEarned = 0;
      let cappedHours = actualHours;

      if (paymentType === 'hourly') {
        // Use overtimeService to calculate with rounding rules and cap
        const calculation = overtimeService.calculateCappedPayment(actualHours, estimatedHours, paymentAmount);
        cappedHours = calculation.cappedHours;
        amountEarned = calculation.basePay;
        hasOvertime = calculation.hasOvertime;

        if (hasOvertime) {
          overtimeInfo = {
            estimatedHours: calculation.estimatedHours,
            actualHours: calculation.billableHours,
            overtimeHours: calculation.overtimeHours,
            hourlyRate: calculation.hourlyRate,
            basePay: calculation.basePay,
            potentialOvertimePay: calculation.overtimeAmount,
            totalPotential: calculation.totalPotential
          };
        }
      } else {
        // Fixed rate - full amount regardless of hours
        amountEarned = paymentAmount;
      }

      // Update the timesheet with current/edited times and recalculated amounts
      const { error: timesheetError } = await supabase
        ?.from('timesheets')
        ?.update({
          clock_in_time: data.clock_in_time,
          clock_out_time: data.clock_out_time,
          total_hours: parseFloat(actualHours.toFixed(2)),
          capped_hours: parseFloat(cappedHours.toFixed(2)),
          base_amount: parseFloat(amountEarned.toFixed(2)),
          amount_earned: parseFloat(amountEarned.toFixed(2)),
          estimated_hours: estimatedHours,
          status: 'pending', // Reset to pending for re-review
          updated_at: new Date().toISOString()
        })
        ?.eq('id', existingTimesheet.id);

      if (timesheetError) {
        console.warn('Failed to update timesheet:', timesheetError);
        // Don't throw - the main update succeeded, timesheet update is secondary
      }
    }

    // Return data along with overtime info so the UI can show the overtime modal
    return {
      instance: data,
      hasOvertime,
      overtimeInfo,
      timesheetId: existingTimesheet?.id
    };
  },

  // Update work notes
  async updateWorkNotes(instanceId, workNotes) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('job_instances')?.update({ work_notes: workNotes })?.eq('id', instanceId)?.eq('assigned_contractor', user?.id)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Update checklist
  async updateChecklist(instanceId, checklistData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('job_instances')?.update({ checklist_data: checklistData })?.eq('id', instanceId)?.eq('assigned_contractor', user?.id)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Upload photo with optional thumbnail
  async uploadPhoto(instanceId, file, photoType, thumbnailFile = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Upload original to storage
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${user?.id}/${instanceId}/${photoType}_${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase?.storage?.from('job-photos')?.upload(fileName, file);

    if (uploadError) throw uploadError;

    // Upload thumbnail if provided
    let thumbnailPath = null;
    if (thumbnailFile) {
      thumbnailPath = getThumbnailPath(fileName);
      const { error: thumbError } = await supabase?.storage?.from('job-photos')?.upload(thumbnailPath, thumbnailFile);

      if (thumbError) {
        console.warn('Failed to upload thumbnail:', thumbError);
        // Continue without thumbnail - not a critical failure
      }
    }

    // Create photo record
    const { data, error } = await supabase?.from('job_photos')?.insert({
        job_instance_id: instanceId,
        uploaded_by: user?.id,
        photo_type: photoType,
        storage_path: fileName,
        thumbnail_path: thumbnailPath
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Get photo URL
  async getPhotoUrl(storagePath) {
    if (!storagePath) return null;
    const { data, error } = await supabase?.storage?.from('job-photos')?.createSignedUrl(storagePath, 3600); // 1 hour expiry

    if (error) throw error;
    return data?.signedUrl;
  },

  // Get photo URLs (both full and thumbnail)
  async getPhotoUrls(storagePath, thumbnailPath = null) {
    if (!storagePath) return { url: null, thumbnailUrl: null };

    // If no thumbnail path provided, try to derive it
    const thumbPath = thumbnailPath || getThumbnailPath(storagePath);

    // Fetch both URLs in parallel
    const [fullUrlResult, thumbUrlResult] = await Promise.all([
      supabase?.storage?.from('job-photos')?.createSignedUrl(storagePath, 3600),
      supabase?.storage?.from('job-photos')?.createSignedUrl(thumbPath, 3600),
    ]);

    const url = fullUrlResult?.data?.signedUrl || null;
    // Fall back to full URL if thumbnail doesn't exist
    const thumbnailUrl = thumbUrlResult?.data?.signedUrl || url;

    return { url, thumbnailUrl };
  },

  // Get job photos
  async getJobPhotos(instanceId) {
    const { data, error } = await supabase?.from('job_photos')?.select('*')?.eq('job_instance_id', instanceId)?.order('uploaded_at', { ascending: true });

    if (error) throw error;

    // Get signed URLs for photos (both full and thumbnail)
    const photosWithUrls = await Promise.all(
      data?.map(async (photo) => {
        const { url, thumbnailUrl } = await this.getPhotoUrls(photo?.storage_path, photo?.thumbnail_path);
        return {
          id: photo?.id,
          jobInstanceId: photo?.job_instance_id,
          photoType: photo?.photo_type,
          storagePath: photo?.storage_path,
          thumbnailPath: photo?.thumbnail_path,
          url,
          thumbnailUrl,
          uploadedAt: photo?.uploaded_at
        };
      })
    );

    return photosWithUrls;
  },

  // Delete a photo (including thumbnail)
  async deletePhoto(photoId, storagePath, thumbnailPath = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Build list of paths to delete (original + thumbnail if exists)
    const pathsToDelete = [storagePath];
    if (thumbnailPath) {
      pathsToDelete.push(thumbnailPath);
    } else {
      // Try to delete derived thumbnail path even if not stored in DB
      const derivedThumbPath = getThumbnailPath(storagePath);
      if (derivedThumbPath) {
        pathsToDelete.push(derivedThumbPath);
      }
    }

    // Delete from storage bucket
    const { error: storageError } = await supabase?.storage?.from('job-photos')?.remove(pathsToDelete);
    if (storageError) {
      console.warn('Failed to delete from storage:', storageError);
      // Continue to delete the record even if storage delete fails
    }

    // Delete from job_photos table
    const { error } = await supabase?.from('job_photos')?.delete()?.eq('id', photoId)?.eq('uploaded_by', user?.id);
    if (error) throw error;

    return { success: true };
  },

  // Save progress (auto-save work notes and checklist)
  async saveProgress(instanceId, { workNotes, checklistData, questionnaireResponses }) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updates = {};
    if (workNotes !== undefined) updates.work_notes = workNotes;
    if (checklistData !== undefined) updates.checklist_data = checklistData;
    if (questionnaireResponses !== undefined) updates.questionnaire_responses = questionnaireResponses;

    if (Object.keys(updates).length === 0) return null;

    const { data, error } = await supabase?.from('job_instances')?.update(updates)?.eq('id', instanceId)?.eq('assigned_contractor', user?.id)?.select()?.single();

    if (error) throw error;
    return data;
  }
};